<script lang="ts">
export default {
  name: 'DownloadMenu',
};
</script>

<script setup lang="ts">
import { ref, watch, nextTick, onUnmounted } from 'vue';
import DownloadButton from '@/components/v2/DownloadButton.vue';
import Tooltip from '@/components/v2/Tooltip.vue';
import { useAnchoredMenuPosition } from '@/composables/useAnchoredMenuPosition';

export interface DownloadMenuItem {
  label: string;
  value: string;
}

const props = withDefaults(
  defineProps<{
    items: DownloadMenuItem[];
    tooltip?: string;
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
    disabled?: boolean;
    /** Align menu with the start (left) of the trigger. Default is end (right-aligned). */
    start?: boolean;
    end?: boolean;
    /** Menu opens above the trigger. */
    top?: boolean;
    /** Menu opens below the trigger (default). */
    bottom?: boolean;
    /** Menu opens to the left of the trigger. */
    left?: boolean;
    /** Menu opens to the right of the trigger. */
    right?: boolean;
  }>(),
  {
    disabled: false,
    tooltipPosition: 'top',
  },
);

const emit = defineEmits<{
  (e: 'export', value: string, finishExport: () => void): void;
}>();

const menuOpen = ref<boolean>(false);
const isExporting = ref<boolean>(false);
const triggerRef = ref<HTMLElement | null>(null);
const listRef = ref<HTMLUListElement | null>(null);
const { menuStyle, updateMenuPosition } = useAnchoredMenuPosition(triggerRef, listRef, props, { capHeight: true });

function handleExport(item: DownloadMenuItem) {
  isExporting.value = true;
  menuOpen.value = false;
  emit('export', item.value, () => (isExporting.value = false));
}

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}

function closeMenu() {
  menuOpen.value = false;
}

function handleClickOutside(e: MouseEvent) {
  const target = e.target as Node;
  if (triggerRef.value?.contains(target) || listRef.value?.contains(target)) return;
  closeMenu();
}

let clickOutsideCleanup: (() => void) | null = null;

watch(menuOpen, (value) => {
  if (!value) {
    menuStyle.value = null;
    clickOutsideCleanup?.();
    clickOutsideCleanup = null;
    return;
  }
  nextTick(() => {
    updateMenuPosition();
    const onScroll = (e: Event) => {
      if (listRef.value?.contains(e.target as Node)) return;
      closeMenu();
    };
    clickOutsideCleanup = () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('scroll', onScroll, true);
    };
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('scroll', onScroll, true);
  });
});

onUnmounted(() => {
  clickOutsideCleanup?.();
});
</script>

<template>
  <div class="relative flex flex-col">
    <div
      ref="triggerRef"
      class="inline-flex">
      <Tooltip
        v-if="tooltip"
        :text="tooltip"
        :position="tooltipPosition">
        <DownloadButton
          :disabled="disabled || isExporting"
          :loading="isExporting"
          @click="toggleMenu" />
      </Tooltip>
      <DownloadButton
        v-else
        :disabled="disabled || isExporting"
        :loading="isExporting"
        @click="toggleMenu" />
    </div>

    <Teleport to="body">
      <ul
        v-if="menuOpen"
        ref="listRef"
        tabindex="0"
        class="fixed z-9999 flex min-w-max flex-col rounded-lg border border-solid border-gray-200 bg-base-100 px-1 py-1 shadow-lg"
        :class="menuStyle?.maxHeight ? 'overscroll-contain overflow-y-auto' : ''"
        :style="menuStyle || undefined"
        @click.stop>
        <li
          v-for="item in items"
          :key="item.value"
          class="cursor-pointer px-2 py-2 text-sm hover:bg-gray-100"
          @click="handleExport(item)">
          {{ item.label }}
        </li>
      </ul>
    </Teleport>
  </div>
</template>
